<script lang="ts" setup>
import AppButtonIcon from '@/components/atoms/buttons/AppButtonIcon.vue'
import AppInput from '@/components/atoms/inputs/AppInput.vue'
import Editor from 'primevue/editor'
import { ref, watch } from 'vue'
import { Save, X } from 'lucide-vue-next'

interface Props {
  showForm: boolean
  infoNote: {
    id: string
    content: string
    user_id?: string | null
    title: string
    color?: string | null
    created_at: string
    updated_at?: string
    position?: number
  } | null
}
const props = withDefaults(defineProps<Props>(), {
  showForm: false,
  infoNote: null,
})
const emit = defineEmits(['closeForm', 'saveNote', 'editNote'])

const localNote = ref({
  title: '',
  content: '',
  user_id: '',
  color: '#3B82F6',
})

const resetForm = () => {
  localNote.value = {
    title: '',
    content: '',
    user_id: '',
    color: '#3B82F6',
  }
}

watch(
  () => props.infoNote,
  (newNote) => {
    if (newNote) {
      localNote.value = {
        title: newNote.title || '',
        content: newNote.content || '',
        user_id: newNote.user_id || '',
        color: newNote.color || '#3B82F6',
      }
    } else if (props.showForm) {
      resetForm()
    }
  },
  { immediate: true },
)

watch(
  () => props.showForm,
  (isVisible) => {
    if (!isVisible || !props.infoNote) {
      resetForm()
    }
  },
)

const handleSave = () => {
  if (props.infoNote) {
    emit('editNote', { ...props.infoNote, ...localNote.value })
  } else {
    emit('saveNote', localNote.value)
  }
}
</script>
<template>
  <div class="flex flex-col gap-1 text-left">
    <span class="text-sm text-gray-600 dark:text-gray-400 font-medium mt-2">
      {{ $t('notes.title_label') }}
    </span>
    <AppInput
      v-model="localNote.title"
      :placeholder="$t('notes.placeholder_title')"
      size="small"
      class="w-full"
    >
      <template #icon>
        <i class="pi pi-pencil" />
      </template>
    </AppInput>
    <span class="text-sm text-gray-600 dark:text-gray-400 font-medium mt-2">
      {{ $t('notes.content_label') }}
    </span>
    <Editor
      v-model="localNote.content"
      :placeholder="$t('notes.placeholder_content')"
      class="notes-editor w-full shadow-sm! rounded-lg"
      editorStyle="height: 150px"
    >
      <template v-slot:toolbar>
        <span class="ql-formats">
          <!-- Botones estándar -->
          <button v-tooltip.bottom="'Bold'" class="ql-bold"></button>
          <button v-tooltip.bottom="'Italic'" class="ql-italic"></button>
          <button v-tooltip.bottom="'Underline'" class="ql-underline"></button>

          <!-- Selectores de Color (Desplegables) -->
          <select v-tooltip.bottom="'Color'" class="ql-color"></select>
          <select v-tooltip.bottom="'Background'" class="ql-background"></select>
        </span>

        <span class="ql-formats">
          <!-- Desplegable de Tamaños o Formatos (Reemplaza al ql-select roto) -->
          <select v-tooltip.bottom="'Size'" class="ql-size">
            <option value="small">Pequeño</option>
            <option selected>Normal</option>
            <option value="large">Grande</option>
            <option value="huge">Gigante</option>
          </select>
        </span>

        <span class="ql-formats">
          <!-- Listas y Enlaces -->
          <button v-tooltip.bottom="'List'" class="ql-list" value="ordered"></button>
          <button v-tooltip.bottom="'Link'" class="ql-link"></button>
        </span>
      </template>
    </Editor>
    <div class="flex items-center gap-3">
      <span class="text-sm text-gray-600 dark:text-gray-400 font-medium mt-2">
        {{ $t('notes.color') }}
      </span>
      <label
        class="relative mt-2 block h-6 w-6 cursor-pointer overflow-hidden rounded-md border border-gray-300 shadow-sm ring-offset-2 transition hover:scale-105 focus-within:ring-2 focus-within:ring-[var(--p-primary-color)] dark:border-gray-600"
        :style="{ backgroundColor: localNote.color }"
      >
        <input
          v-model="localNote.color"
          type="color"
          class="absolute inset-0 h-12 w-12 -translate-x-2 -translate-y-2 cursor-pointer opacity-0"
          aria-label="Seleccionar color"
        />
      </label>
      <span class="mt-2 text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
        {{ localNote.color }}
      </span>
    </div>
  </div>
  <div class="flex justify-end gap-2 mt-6">
    <AppButtonIcon
      severity="secondary"
      :label="$t('general.cancel')"
      @click="emit('closeForm')"
    >
      <template #icon>
        <X :size="16" />
      </template>
    </AppButtonIcon>
    <AppButtonIcon
      v-show="localNote.title && localNote.content"
      severity="primary"
      :label="$t('general.save')"
      @click="handleSave"
    >
      <template #icon>
        <Save :size="16" />
      </template>
    </AppButtonIcon>
  </div>
</template>

<style scoped>
.notes-editor :deep(.p-editor-container),
.notes-editor :deep(.p-editor),
.notes-editor :deep(.p-editor-toolbar),
.notes-editor :deep(.p-editor-content),
.notes-editor :deep(.ql-container),
.notes-editor :deep(.ql-editor) {
  background: transparent !important;
}

.notes-editor :deep(.p-editor-container) {
  border-color: color-mix(in srgb, white 45%, transparent) !important;
}

.notes-editor :deep(.p-editor-toolbar),
.notes-editor :deep(.p-editor-content) {
  border-color: color-mix(in srgb, white 45%, transparent) !important;
}

:global(html.dark) .notes-editor :deep(.p-editor-container),
:global(html.dark) .notes-editor :deep(.p-editor-toolbar),
:global(html.dark) .notes-editor :deep(.p-editor-content) {
  border-color: color-mix(in srgb, var(--p-primary-900) 85%, transparent) !important;
}

.notes-editor :deep(.ql-editor) {
  color: var(--p-form-field-color);
}

.notes-editor :deep(.ql-editor.ql-blank::before) {
  color: var(--p-form-field-placeholder-color);
  font-style: normal;
}

.notes-editor :deep(.ql-stroke) {
  stroke: currentColor;
}

.notes-editor :deep(.ql-fill) {
  fill: currentColor;
}

:global(html.dark) .notes-editor :deep(.ql-toolbar),
:global(html.dark) .notes-editor :deep(.ql-picker-label),
:global(html.dark) .notes-editor :deep(.ql-picker-options) {
  color: var(--p-text-color);
}

:global(html.dark) .notes-editor :deep(.ql-picker-options) {
  background: var(--p-surface-800) !important;
  border-color: var(--p-surface-700) !important;
}

:global(html.dark) .notes-editor :deep(.ql-editor) {
  color: var(--p-surface-100);
}

:global(html.dark) .notes-editor :deep(.ql-editor.ql-blank::before) {
  color: var(--p-surface-400);
}
</style>
